import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DoCheck,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms';
import { MatError, matFormFieldAnimations } from '@angular/material/form-field';
import {
  defer,
  merge,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';

import { FormFieldControlDirective } from '../../form-field/form-field-control.directive';
import {
  SelectBoxComponent,
  SelectBoxSelectionChange,
} from '../select-box.component';

/** Change event object that is emitted when the select value has changed. */
export class SelectBoxChange {
  constructor(
    /** Reference to the select that emitted the change event. */
    public source: SelectBoxGroupComponent,
    /** Current value of the select that emitted the event. */
    public value: unknown
  ) {}
}

@Component({
    selector: 'lui-select-box-group',
    templateUrl: './select-box-group.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SelectBoxGroupComponent,
        },
        {
            provide: FormFieldControlDirective,
            useExisting: SelectBoxGroupComponent,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [matFormFieldAnimations.transitionMessages],
    standalone: false
})
export class SelectBoxGroupComponent
  implements
    ControlValueAccessor,
    FormFieldControlDirective<unknown>,
    OnInit,
    AfterContentInit,
    DoCheck,
    OnDestroy
{
  @HostBinding('class') className = 'lui-select-box-group';

  @HostBinding('class.lui-select-box-group-has-error') get errorClass() {
    return this.ngControl && this.ngControl.invalid;
  }

  @HostBinding('class.lui-select-box-group-touched') get touchedClass() {
    return this.ngControl && this.ngControl.touched;
  }

  @HostBinding('class.lui-select-box-group-disabled') get disabledClass() {
    return this.disabled;
  }

  @ContentChildren(MatError) matErrors!: QueryList<MatError>;

  @ContentChildren(SelectBoxComponent, { descendants: true })
  selectBoxes!: QueryList<SelectBoxComponent>;

  /** Whether the user should be allowed to select multiple options. */
  @Input()
  get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: BooleanInput) {
    this._multiple = coerceBooleanProperty(value);
  }
  private _multiple = false;

  /** Value of the select control. */
  @Input()
  get value(): unknown {
    return this._value;
  }
  set value(newValue: unknown) {
    const hasAssigned = this._assignValue(newValue);

    if (hasAssigned) {
      this.onChange(newValue);
    }
  }
  private _value: unknown;

  @Input()
  get required(): boolean {
    return (
      this._required ??
      this.ngControl?.control?.hasValidator(Validators.required) ??
      false
    );
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
  }
  protected _required: boolean | undefined;

  @Input()
  get disabled(): boolean {
    return this._disabled ?? this.ngControl?.control?.disabled ?? false;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this.disabledChanges.next();
    this.stateChanges.next();
  }
  protected _disabled: boolean | undefined;

  /**
   * Function to compare the option values with the selected values. The first argument
   * is a value from an option. The second is a value from the selection. A boolean
   * should be returned.
   */
  @Input()
  get compareWith() {
    return this._compareWith;
  }
  set compareWith(fn: (o1: unknown, o2: unknown) => boolean) {
    this._compareWith = fn;
    if (this._selectionModel) {
      // A different comparator means the selection could change.
      this._initializeSelection();
    }
  }

  @Input() canDeselect = true;

  /** Event emitted when the selected value has been changed by the user. */
  @Output() readonly selectionChange: EventEmitter<SelectBoxChange> =
    new EventEmitter<SelectBoxChange>();

  @Output() readonly valueChange: EventEmitter<unknown> =
    new EventEmitter<unknown>();

  /** The currently selected option. */
  get selected(): SelectBoxComponent | SelectBoxComponent[] {
    return this.multiple
      ? this._selectionModel?.selected || []
      : this._selectionModel?.selected[0];
  }

  _selectionModel = new SelectionModel<SelectBoxComponent>();

  touched = false;

  selectedSubscription!: Subscription;

  initialised!: boolean;

  _destroy$ = new Subject<void>();

  /** Combined stream of all of the child options' change events. */
  readonly optionSelectionChanges: Observable<SelectBoxSelectionChange> = defer(
    () => {
      const options = this.selectBoxes;

      return options.changes.pipe(
        startWith(options),
        switchMap(() =>
          merge(...options.map((option) => option.selectionChange))
        )
      );
    }
  ) as Observable<SelectBoxSelectionChange>;

  _injector = inject(Injector);

  ngControl!: NgControl | null;

  readonly disabledChanges: Subject<void> = new Subject<void>();
  readonly stateChanges: Subject<void> = new Subject<void>();
  readonly empty!: boolean;
  readonly errorState!: boolean;
  readonly focused!: boolean;

  @HostListener('focus') onFocus() {
    this.stateChanges.next();
  }

  @HostListener('blur') onBlur() {
    this.cd.markForCheck();
  }

  private _compareWith = (o1: unknown, o2: unknown) => o1 === o2;

  onChange: (value: unknown | unknown[]) => void = () => undefined;

  onTouched: () => void = () => undefined;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
    });

    this._selectionModel = new SelectionModel<SelectBoxComponent>(
      this.multiple
    );
  }

  ngDoCheck(): void {
    if (this.ngControl && this.ngControl.touched !== this.touched) {
      this.stateChanges.next();
    }
  }

  ngAfterContentInit(): void {
    this._selectionModel.changed
      .pipe(takeUntil(this._destroy$))
      .subscribe((event) => {
        event.added.forEach((option) => option.select());
        event.removed.forEach((option) => option.deselect());
      });

    this.selectBoxes.changes
      .pipe(startWith(null), takeUntil(this._destroy$))
      .subscribe(() => {
        this._resetAndSubToOptionChanges();
        this._initializeSelection();
      });

    merge(this.selectBoxes.changes, this.disabledChanges)
      .pipe(startWith(null), takeUntil(this._destroy$))
      .subscribe(() => {
        this._syncDisabledState();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  writeValue(value: unknown) {
    this._assignValue(value);
  }

  registerOnChange(fn: (value: unknown) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  private _resetAndSubToOptionChanges(): void {
    const changedOrDestroyed = merge(this.selectBoxes.changes, this._destroy$);

    this.optionSelectionChanges
      .pipe(takeUntil(changedOrDestroyed))
      .subscribe((event) => {
        this._onSelect(event.source, event.isUserInput);
      });
  }

  private _initializeSelection(): void {
    // Defer setting the value in order to avoid the "Expression
    // has changed after it was checked" errors from Angular.
    Promise.resolve().then(() => {
      if (this.ngControl) {
        this._value = this.ngControl.value;
      }

      this._setSelectionByValue(this._value);
      this.stateChanges.next();
    });
  }

  /** Invoked when an option is clicked. */
  private _onSelect(option: SelectBoxComponent, isUserInput: boolean): void {
    const wasSelected = this._selectionModel.isSelected(option);

    if (isUserInput && this.multiple && wasSelected === option._selected) {
      this._selectionModel.deselect(option);
    }

    if (wasSelected !== option._selected) {
      option._selected
        ? this._selectionModel.select(option)
        : this._selectionModel.deselect(option);
    }

    if (wasSelected !== this._selectionModel.isSelected(option)) {
      this._propagateChanges();
    }

    this.markAsTouched();
    this.stateChanges.next();
  }

  /** Assigns a specific value to the select. Returns whether the value has changed. */
  private _assignValue(newValue: unknown | unknown[]): boolean {
    // Always re-assign an array, because it might have been mutated.
    if (
      newValue !== this._value ||
      (this._multiple && Array.isArray(newValue))
    ) {
      if (this.selectBoxes) {
        this._setSelectionByValue(newValue);
      }
      this._value = newValue;
      return true;
    }
    return false;
  }

  /**
   * Sets the selected option based on a value. If no option can be
   * found with the designated value, the select trigger is cleared.
   */
  private _setSelectionByValue(value: unknown | unknown[]): void {
    this._selectionModel.clear();

    if (this.multiple && value) {
      if (!Array.isArray(value)) {
        throw new Error('Selected multiple but value not array');
      }

      value.forEach((currentValue: unknown) =>
        this._selectOptionByValue(currentValue)
      );
    } else {
      this._selectOptionByValue(value);
    }
  }

  /**
   * Finds and selects and option based on its value.
   * @returns Option that has the corresponding value.
   */
  private _selectOptionByValue(value: unknown): SelectBoxComponent | undefined {
    const correspondingOption = this.selectBoxes.find(
      (option: SelectBoxComponent) => {
        // Skip options that are already in the model. This allows us to handle cases
        // where the same primitive value is selected multiple times.
        if (this._selectionModel.isSelected(option)) {
          return false;
        }

        try {
          // Treat null as a special reset value.
          return this._compareWith(option.value, value);
        } catch (error) {
          console.warn(error);
          return false;
        }
      }
    );

    if (correspondingOption) {
      this._selectionModel.select(correspondingOption);
    }

    return correspondingOption;
  }

  /** Emits change event to set the model value. */
  private _propagateChanges(fallbackValue?: unknown): void {
    let valueToEmit: unknown = null;

    if (this.multiple) {
      valueToEmit = (this.selected as SelectBoxComponent[]).map(
        (option) => option.value
      );
    } else {
      valueToEmit = this.selected
        ? (this.selected as SelectBoxComponent).value
        : fallbackValue;
    }

    this._value = valueToEmit;
    this.valueChange.emit(valueToEmit);
    this.onChange(valueToEmit);
    this.selectionChange.emit(this._getChangeEvent(valueToEmit));
  }

  protected _getChangeEvent(value: unknown) {
    return new SelectBoxChange(this, value);
  }

  private _syncDisabledState() {
    this.selectBoxes.forEach((item) => {
      item.toggleDisableStateOverride(this.disabled);
    });
  }
}
